import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Star, CheckCircle, Loader } from 'lucide-react';
import { feedbackService } from '../services/feedback';

export const PublicFeedback: React.FC = () => {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const labels = ['', 'Terrible', 'Poor', 'Okay', 'Good', 'Excellent'];

  const submit = async () => {
    if (!restaurantId) return;
    if (rating < 1) { setError('Please tap a star to rate us.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await feedbackService.submitPublic(restaurantId, {
        rating,
        comment: comment.trim() || undefined,
        customerName: name.trim() || undefined,
        customerMobile: mobile.trim() || undefined,
        source: 'QR',
      });
      setDone(true);
    } catch {
      setError('Could not submit your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-indigo-50 p-6">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="h-11 w-11 text-green-500" />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Thank you!</h1>
          <p className="text-gray-500 text-sm leading-relaxed">
            Your feedback has been received. We truly appreciate you taking the time to help us improve.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-indigo-50 p-4">
      <div className="bg-white rounded-3xl shadow-xl p-7 sm:p-9 max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-extrabold text-gray-900">How was your experience?</h1>
          <p className="text-gray-500 text-sm mt-1">Your feedback helps us serve you better.</p>
        </div>

        {/* Stars */}
        <div className="flex justify-center gap-2 mb-2">
          {[1, 2, 3, 4, 5].map(i => {
            const active = (hover || rating) >= i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => { setRating(i); setError(''); }}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(0)}
                className="p-1 transition-transform active:scale-90"
              >
                <Star className={`h-10 w-10 ${active ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
              </button>
            );
          })}
        </div>
        <p className="text-center text-sm font-semibold h-5 mb-5 text-amber-500">{labels[hover || rating]}</p>

        {/* Comment */}
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Tell us more (optional)"
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none mb-3"
        />

        {/* Optional contact */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Name (optional)"
            className="px-4 py-3 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <input
            value={mobile}
            onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="Mobile (optional)"
            inputMode="numeric"
            className="px-4 py-3 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {error && <p className="text-sm text-red-500 text-center mb-3">{error}</p>}

        <button
          onClick={submit}
          disabled={submitting}
          className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-bold rounded-xl transition-colors text-sm flex items-center justify-center gap-2"
        >
          {submitting ? <><Loader className="h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
};
